import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostHonorarVereinbarungRequest, IPostHonorarVereinbarungResponse } from 'fa-kt.types';
import { PostHonorarVereinbarungCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-honorar-vereinbarung',
  templateUrl: './post-honorar-vereinbarung.component.html',
  styleUrls: ['./post-honorar-vereinbarung.component.css'],
})
export class PostHonorarVereinbarungCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostHonorarVereinbarungRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostHonorarVereinbarungRequest> = new EventEmitter<IPostHonorarVereinbarungRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostHonorarVereinbarungResponse> = new EventEmitter<IPostHonorarVereinbarungResponse>();

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postHonorarVereinbarung: PostHonorarVereinbarungCommandService /* << more    */ /*    more >> */,
  ) {
    this.command = sitemap.FINANZEN.Commands.POST_HONORAR_VEREINBARUNG;
    this.form = this.fb.group({
      honorarVereinbarung: this.fb.group({
        id: [null, []],
        kunde: [null, [Validators.required]],
        bisMarktwert: [null, []],
        lohnWohnen: [null, []],
        lohnGewerbe: [null, []],
        lohnSpezial: [null, []],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postHonorarVereinbarung.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostHonorarVereinbarungRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.honorarVereinbarungId?.valueChanges.subscribe(async (id) => {
        /* << on-honorar-vereinbarung-id-change    */
        /*    on-honorar-vereinbarung-id-change >> */
      }) as Subscription,
      this.honorarVereinbarungKunde?.valueChanges.subscribe(async (kunde) => {
        /* << on-honorar-vereinbarung-kunde-change    */
        /*    on-honorar-vereinbarung-kunde-change >> */
      }) as Subscription,
      this.honorarVereinbarungBisMarktwert?.valueChanges.subscribe(async (bisMarktwert) => {
        /* << on-honorar-vereinbarung-bis-marktwert-change    */
        /*    on-honorar-vereinbarung-bis-marktwert-change >> */
      }) as Subscription,
      this.honorarVereinbarungLohnWohnen?.valueChanges.subscribe(async (lohnWohnen) => {
        /* << on-honorar-vereinbarung-lohn-wohnen-change    */
        /*    on-honorar-vereinbarung-lohn-wohnen-change >> */
      }) as Subscription,
      this.honorarVereinbarungLohnGewerbe?.valueChanges.subscribe(async (lohnGewerbe) => {
        /* << on-honorar-vereinbarung-lohn-gewerbe-change    */
        /*    on-honorar-vereinbarung-lohn-gewerbe-change >> */
      }) as Subscription,
      this.honorarVereinbarungLohnSpezial?.valueChanges.subscribe(async (lohnSpezial) => {
        /* << on-honorar-vereinbarung-lohn-spezial-change    */
        /*    on-honorar-vereinbarung-lohn-spezial-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostHonorarVereinbarungRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.honorarVereinbarung) {
        value.honorarVereinbarung = cleanObject(value.honorarVereinbarung);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.honorarVereinbarung) {
        payload.honorarVereinbarung = cleanObject(payload.honorarVereinbarung);
      }
      const response: IPostHonorarVereinbarungResponse = await this.postHonorarVereinbarung.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  /* << methods    */
  get kundenAnfertigungBeruecksichtigen() {
    return this.form.controls.honorarVereinbarung.get('kundenAnfertigungBeruecksichtigen');
  }
  get objektNutzungenValue() {
    return this.form.controls.honorarVereinbarung.get('objektNutzungen')?.value || '*';
  }
  get honorarVereinbarung() {
    return this.form.controls.honorarVereinbarung.get('honorarVereinbarung');
  }
  /*    methods >> */

  get honorarVereinbarungId() {
    return this.form.controls.honorarVereinbarung.get('id');
  }
  get honorarVereinbarungKunde() {
    return this.form.controls.honorarVereinbarung.get('kunde');
  }
  get honorarVereinbarungBisMarktwert() {
    return this.form.controls.honorarVereinbarung.get('bisMarktwert');
  }
  get honorarVereinbarungLohnWohnen() {
    return this.form.controls.honorarVereinbarung.get('lohnWohnen');
  }
  get honorarVereinbarungLohnGewerbe() {
    return this.form.controls.honorarVereinbarung.get('lohnGewerbe');
  }
  get honorarVereinbarungLohnSpezial() {
    return this.form.controls.honorarVereinbarung.get('lohnSpezial');
  }
}
